/* ValidationErrorBanner — Phase C · C3c validation slot renderer
 *
 * Renders validation errors emitted by the rules engine (`result.validation_errors[]`)
 * as a red banner above the Components tab. Errors with severity='error'
 * are BLOCKING · display a "Save disabled" hint (actual disable is wired
 * on the button via `hasBlockingErrors` flag from useCascadeEngine).
 *
 * Message shape (from `_emit_validation_error`):
 *   { code, field, severity, message, rule_id }
 *
 * Design principle: banner is compact when 0 errors (hidden),
 * expanded when 1+ errors · never scrolls · sits directly above the
 * first section of the tab so operators see it before they scroll.
 */
import React from 'react';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';

const SEVERITY_STYLE = {
  error:   { icon: AlertCircle,    ring: 'ring-red-300',    bg: 'bg-red-50',    text: 'text-red-900',    accent: 'text-red-600' },
  warning: { icon: AlertTriangle,  ring: 'ring-amber-300',  bg: 'bg-amber-50',  text: 'text-amber-900',  accent: 'text-amber-600' },
  info:    { icon: Info,           ring: 'ring-sky-300',    bg: 'bg-sky-50',    text: 'text-sky-900',    accent: 'text-sky-600' },
};

export default function ValidationErrorBanner({ errors = [], onDismiss = null }) {
  if (!errors || errors.length === 0) return null;

  // Group by severity — errors first
  const bySeverity = { error: [], warning: [], info: [] };
  for (const e of errors) {
    const s = (e.severity || 'error').toLowerCase();
    (bySeverity[s] || bySeverity.error).push(e);
  }
  const hasBlocking = bySeverity.error.length > 0;
  const worstSeverity = hasBlocking ? 'error' : (bySeverity.warning.length ? 'warning' : 'info');
  const style = SEVERITY_STYLE[worstSeverity];
  const Icon = style.icon;

  const label = hasBlocking
    ? `${bySeverity.error.length} issue${bySeverity.error.length > 1 ? 's' : ''} blocking save`
    : `${errors.length} advisor${errors.length > 1 ? 'ies' : 'y'}`;

  return (
    <div
      data-testid="validation-error-banner"
      className={`mb-4 px-4 py-3 rounded-lg border shadow-sm ring-1 ${style.ring} ${style.bg} ${style.text}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${style.accent}`} />
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-sm" data-testid="validation-banner-title">
              {label}
            </h3>
            {onDismiss && !hasBlocking && (
              <button
                onClick={onDismiss}
                className="text-xs text-slate-500 hover:text-slate-800 underline"
                data-testid="validation-banner-dismiss"
              >
                Dismiss
              </button>
            )}
          </div>

          <ul className="space-y-1.5">
            {['error', 'warning', 'info'].flatMap(sev =>
              bySeverity[sev].map((e, i) => (
                <li
                  key={`${sev}-${i}`}
                  data-testid={`validation-error-${e.code || 'unknown'}`}
                  className="text-sm flex items-start gap-2"
                >
                  <span
                    className={`inline-block mt-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wide ${
                      sev === 'error' ? 'bg-red-200 text-red-800' :
                      sev === 'warning' ? 'bg-amber-200 text-amber-800' :
                      'bg-sky-200 text-sky-800'
                    }`}
                  >
                    {sev}
                  </span>
                  <div className="flex-1">
                    <p>{e.message || e.code || 'Validation issue'}</p>
                    {(e.field || e.rule_id) && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {e.field && <>Field: <code className="bg-white/70 px-1 rounded">{e.field}</code>{'  ·  '}</>}
                        <span className="opacity-60">rule <code>{e.rule_id}</code></span>
                      </p>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>

          {hasBlocking && (
            <p className="text-xs text-red-700 pt-1 mt-1 border-t border-red-200" data-testid="validation-banner-blocking-note">
              💾 Save is disabled until every blocking issue is resolved.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
